import { Link, useNavigate, useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { supabase, getStoredUser } from "../supabase";
import PostTime, { postNameColumn } from "../components/PostTime";
import DownloadButton from "../components/DownloadButton";
import LikeButton from "../components/LikeButton";
import SaveButton from "../components/SaveButton";
import Comments from "../components/Comments";
import ImageLightbox from "../components/ImageLightbox";
import TruncatedText from "../components/TruncatedText";
import ImageCarousel from "../components/ImageCarousel";
import ShareButton from "../components/ShareButton";
import LazyImage from "../components/LazyImage";
import { IoPricetagOutline, IoPricetag, IoFlag, IoEllipsisVertical } from "react-icons/io5";
import { PiChatCircleBold } from "react-icons/pi";
import { getBlockedUserIds } from "../utils/blocks";
import { getMutedUserIds } from "../utils/mutes";
import PostSkeleton from "../components/PostSkeleton";
import { likeDesign } from "../utils/likes";
import { fetchMyEngagement, subscribeToMyEngagement } from "../utils/myEngagement";
import { subscribeToStatsPolling } from "../utils/statsPolling";
import ReportModal from "../components/ReportModal";
import PostMenu from "../components/PostMenu";
import VerifiedBadge from "../components/VerifiedBadge";
import TagInfoModal from "../components/TagInfoModal";
import { confirmLogin } from "../utils/dialog";
import { useLanguage } from "../context/LanguageContext";

const PAGE_SIZE = 10;

export default function HashtagPage() {
  const { t } = useLanguage();
  const { tag: rawTag } = useParams();
  // Hashtags are always stored/linked lowercase (see utils/hashtags.js and
  // MentionText.jsx) — a normal in-app hashtag click already lands here
  // lowercase, but a hand-typed or externally shared URL like /tag/Phulkari
  // wouldn't be, and would otherwise match nothing despite the tag having
  // posts under its lowercase form.
  const tag = rawTag?.toLowerCase() || "";
  const navigate = useNavigate();

  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(getStoredUser);
  const [blockedIds, setBlockedIds] = useState([]);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [tagModalId, setTagModalId] = useState(null);
  const [reportModalId, setReportModalId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);
  const [openComments, setOpenComments] = useState({});

  // Close ⋮ menu when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const sentinelRef = useRef(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // The tag this page is currently showing results for — loadMore reads
  // this to tell whether the tag changed out from under an in-flight
  // "load more" fetch (see loadMore below).
  const activeTagRef = useRef(tag);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    async function getUser() {
      const {
        data: { session }
      } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
    }

    getUser();
  }, []);

  useEffect(() => {
    let cancelled = false;
    activeTagRef.current = tag;

    async function fetchByTag() {
      setLoading(true);

      const {
        data: { session }
      } = await supabase.auth.getSession();
      const currentUser = session?.user ?? null;
      const [blockedRes, mutedRes] = currentUser
        ? await Promise.all([getBlockedUserIds(currentUser.id), getMutedUserIds(currentUser.id)])
        : [[], []];
      const blocked = [...new Set([...blockedRes, ...mutedRes])];

      if (cancelled) return;
      setBlockedIds(blocked);

      let query = supabase
        .from("designs")
        .select("*, users(username, avatar_url, verified)")
        .contains("hashtags", [tag])
        .order("created_at", { ascending: false })
        .range(0, PAGE_SIZE - 1);

      if (blocked.length > 0) {
        query = query.not("user_id", "in", `(${blocked.join(",")})`);
      }

      const { data, error } = await query;

      // Clicking another #hashtag link while this was still loading (same
      // route, just a different :tag param — the page doesn't remount) used
      // to let whichever request finished last overwrite the list, even
      // when it was for the tag no longer showing in the heading above it.
      if (cancelled) return;

      if (error) {
        console.log("HASHTAG DESIGNS ERROR:", error);
        setLoading(false);
        return;
      }

      let list = data || [];
      if (currentUser) {
        const { likedIds, savedIds } = await fetchMyEngagement(list.map(d => d.id), currentUser.id);
        if (cancelled) return;
        list = list.map(d => ({ ...d, _likedByMe: likedIds.has(d.id), _savedByMe: savedIds.has(d.id) }));
      }

      setDesigns(list);
      setOffset(PAGE_SIZE);
      setHasMore(list.length === PAGE_SIZE);
      loadingMoreRef.current = false;
      setLoadingMore(false);
      setLoading(false);
    }

    fetchByTag();

    return () => {
      cancelled = true;
    };
  }, [tag]);

  // tag changing (e.g. browser back/forward landing on a different hashtag
  // while this page stays mounted) doesn't unmount the page, so without this
  // a lightbox left open from the previous tag's design would keep showing
  // that design's cached image list even after the page has moved on.
  useEffect(() => {
    setLightboxImage(null);
    setOpenComments({});
  }, [tag]);

  // Reuses the blockedIds fetched once above instead of re-querying the
  // blocks table on every scroll page — a viewer's block list doesn't
  // change mid-session.
  async function loadMore() {
    if (loading || loadingMoreRef.current || !hasMore) return;
    const requestTag = tag;
    loadingMoreRef.current = true;
    setLoadingMore(true);

    let query = supabase
      .from("designs")
      .select("*, users(username, avatar_url, verified)")
      .contains("hashtags", [tag])
      .order("created_at", { ascending: false })
      .range(offset, offset + PAGE_SIZE - 1);

    if (blockedIds.length > 0) {
      query = query.not("user_id", "in", `(${blockedIds.join(",")})`);
    }

    const { data, error } = await query;

    // The tag changed (and fetchByTag already reset the list) while this
    // page was still in flight — appending it now would tack the OLD tag's
    // designs onto the NEW tag's freshly-reset list. Still have to clear
    // our own loadingMore here though: leaving it stuck true would make
    // every future loadMore() call for the NEW tag return immediately
    // (its own guard checks loadingMore), permanently breaking infinite
    // scroll on that tag.
    if (requestTag !== activeTagRef.current) {
      loadingMoreRef.current = false;
      setLoadingMore(false);
      return;
    }

    if (error) {
      console.log("LOAD MORE HASHTAG DESIGNS ERROR:", error);
      loadingMoreRef.current = false;
      setLoadingMore(false);
      return;
    }

    let newDesigns = data || [];
    if (user) {
      const { likedIds, savedIds } = await fetchMyEngagement(newDesigns.map(d => d.id), user.id);
      if (requestTag !== activeTagRef.current) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
        return;
      }
      newDesigns = newDesigns.map(d => ({ ...d, _likedByMe: likedIds.has(d.id), _savedByMe: savedIds.has(d.id) }));
    }

    setDesigns(prev => [...prev, ...newDesigns]);
    setOffset(prev => prev + PAGE_SIZE);
    setHasMore(newDesigns.length === PAGE_SIZE);
    loadingMoreRef.current = false;
    setLoadingMore(false);
  }

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || loading || !hasMore) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "400px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, offset, hasMore, loadingMore]);

  // Keeps the latest `designs` for the poll below to read without a stale
  // closure — it runs on its own timer, not in response to designs changing.
  // Not tied to `tag`: this always reads whatever's currently in state, so
  // it naturally follows along whenever fetchByTag resets it for a new tag.
  const designsRef = useRef(designs);
  useEffect(() => {
    designsRef.current = designs;
  }, [designs]);

  // Refreshes likes/downloads/comment_count for whatever's currently loaded
  // on this page, every 30s — not a realtime listener, since a plain
  // "designs" UPDATE subscription has no way to scope itself to just the
  // posts on screen and would broadcast every design's change to every
  // connected client regardless (see utils/statsPolling.js). Whether *this
  // viewer* liked/saved each one is a separate, per-user concern — see the
  // myEngagement effect below.
  useEffect(() => {
    return subscribeToStatsPolling(
      () => designsRef.current.map(d => d.id),
      rows => {
        const byId = new Map(rows.map(r => [r.id, r]));
        setDesigns(prev =>
          prev.map(d => {
            const fresh = byId.get(d.id);
            return fresh
              ? { ...d, likes: fresh.likes, downloads: fresh.downloads, comment_count: fresh.comment_count }
              : d;
          })
        );
      }
    );
  }, []);

  // Live updates for this viewer's OWN likes/saves — e.g. liking something
  // from another tab or device signed into the same account. Filtered to
  // just this user's rows (see utils/myEngagement.js).
  useEffect(() => {
    if (!user) return undefined;

    const setEngagement = (designId, fields) => {
      setDesigns(prev => prev.map(d => (d.id === designId ? { ...d, ...fields } : d)));
    };

    return subscribeToMyEngagement(user.id, {
      onLike: id => setEngagement(id, { _likedByMe: true }),
      onUnlike: id => setEngagement(id, { _likedByMe: false }),
      onSave: id => setEngagement(id, { _savedByMe: true }),
      onUnsave: id => setEngagement(id, { _savedByMe: false })
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  async function refreshCommentCount(designId) {
    const { count, error } = await supabase
      .from("comments")
      .select("id", { count: "exact", head: true })
      .eq("design_id", designId);

    if (error) {
      console.log("REFRESH COUNT ERROR:", error);
      return;
    }

    setDesigns(prev =>
      prev.map(d => (d.id === designId ? { ...d, comment_count: count ?? d.comment_count } : d))
    );
  }

  function toggleComments(id) {
    const isCurrentlyOpen = !!openComments[id];
    setOpenComments(prev => ({ ...prev, [id]: !isCurrentlyOpen }));
    if (isCurrentlyOpen) refreshCommentCount(id);
  }

  function requireLogin(action) {
    if (user) {
      action();
      return;
    }
    confirmLogin(t("common.logInToContinue"), t("nav.login")).then(goLogin => {
      if (goLogin) navigate("/login");
    });
  }

  return (
    <div style={page}>
      <h1 style={title}>
        <IoPricetagOutline style={{ verticalAlign: -3, marginRight: 6, color: "var(--accent-primary)" }} />
        #{tag}
      </h1>

      {loading ? (
        <PostSkeleton />
      ) : designs.length === 0 ? (
        <p style={mutedText}>No designs with #{tag} yet.</p>
      ) : (
        <div style={grid}>
          {designs.map(design => {
            function updateDesign(fields) {
              setDesigns(prev =>
                prev.map(d => (d.id === design.id ? { ...d, ...fields } : d))
              );
            }

            const commentsOpen = !!openComments[design.id];
            const commentCount = design.comment_count || 0;

            return (
              <div key={design.id} style={card}>
                <div style={cardHeader}>
                  <Link
                    to={design.user_id === user?.id ? "/profile" : `/u/${design.user_id}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      flex: 1,
                      minWidth: 0,
                      textDecoration: "none",
                      color: "var(--text-primary)"
                    }}
                  >
                    <LazyImage
                      src={design.users?.avatar_url || "/default-avatar.svg"}
                      alt={`${design.users?.username || "User"}'s avatar`}
                      style={{ ...cardAvatar, flexShrink: 0 }}
                    />
                    <span style={postNameColumn}>
                      <span style={usernameLink}>
                        @{design.users?.username || "unknown"}
                        {design.users?.verified && <VerifiedBadge />}
                      </span>
                      <PostTime date={design.created_at} />
                    </span>
                  </Link>
                  <div
                    style={{ position: "relative", marginLeft: "auto", flexShrink: 0 }}
                    ref={openMenuId === design.id ? menuRef : null}
                  >
                    <button
                      type="button"
                      style={reportBtn}
                      onClick={() => setOpenMenuId(openMenuId === design.id ? null : design.id)}
                      aria-label="More options"
                    >
                      <IoEllipsisVertical />
                    </button>
                    {openMenuId === design.id && (
                      <PostMenu
                        items={[
                          { icon: <IoPricetag />, label: "Tag info", onClick: () => { setOpenMenuId(null); setTagModalId(design.id); } },
                          ...(user?.id && design.user_id !== user.id
                            ? [{ icon: <IoFlag />, label: "Report", onClick: () => { setOpenMenuId(null); setReportModalId(design.id); } }]
                            : [])
                        ]}
                      />
                    )}
                  </div>
                </div>

                <ImageCarousel
                  images={design.images?.length > 0 ? design.images : [design.image_url]}
                  alt={design.title}
                  imgStyle={{ ...image, cursor: "zoom-in" }}
                  onImageClick={setLightboxImage}
                  onDoubleTap={() =>
                    likeDesign({ designId: design.id, user, designOwnerId: design.user_id, onUpdate: updateDesign, likeOnly: true })
                  }
                />

                <div style={content}>
                  <div style={actions}>
                    <LikeButton
                      designId={design.id}
                      likes={design.likes}
                      liked={design._likedByMe}
                      user={user}
                      designOwnerId={design.user_id}
                      onUpdate={updateDesign}
                    />

                    <button
                      onClick={() => requireLogin(() => toggleComments(design.id))}
                      style={commentBadgeBtn}
                      aria-label="Toggle comments"
                    >
                      <PiChatCircleBold size={27} /> <span style={commentCountText}>{commentCount}</span>
                    </button>

                    <DownloadButton
                      fileURL={design.design_url}
                      files={design.design_files}
                      designId={design.id}
                      downloads={design.downloads}
                      designOwnerId={design.user_id}
                      user={user}
                      onUpdate={updateDesign}
                    />

                    <ShareButton designId={design.id} />

                    <span style={{ display: "inline-flex", marginLeft: "auto" }}>
                      <SaveButton
                        designId={design.id}
                        saved={design._savedByMe}
                        user={user}
                        onUpdate={updateDesign}
                      />
                    </span>
                  </div>

                  <p style={caption}><TruncatedText text={design.title} /></p>
                  {design.description && (
                    <p style={{ margin: "2px 0", color: "var(--text-body)", fontSize: "14px" }}>
                      {design.description}
                    </p>
                  )}

                  {commentsOpen && (
                    <Comments designId={design.id} onClose={() => toggleComments(design.id)} designOwnerId={design.user_id} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}
      {loadingMore && <p style={mutedText}>Loading more…</p>}

      {lightboxImage && (
        <ImageLightbox
          images={lightboxImage.images}
          initialIndex={lightboxImage.index}
          onClose={() => setLightboxImage(null)}
        />
      )}

      {tagModalId && (
        <TagInfoModal designId={tagModalId} onClose={() => setTagModalId(null)} />
      )}

      {reportModalId && (
        <ReportModal
          reporterId={user?.id}
          targetType="design"
          targetId={reportModalId}
          onClose={() => setReportModalId(null)}
        />
      )}
    </div>
  );
}

/* ===== STYLES (Virsa palette, matches Home/Trending) ===== */

const page = {
  padding: "20px 4px calc(90px + env(safe-area-inset-bottom))",
  background: "var(--bg-page)",
  minHeight: "100vh"
};

const title = {
  textAlign: "center",
  margin: "0 0 22px",
  color: "var(--text-heading)"
};

const mutedText = { fontSize: 14, color: "var(--text-muted)", textAlign: "center" };

const grid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
  gap: "10px 18px"
};

const card = {
  background: "var(--bg-surface)",
  borderRadius: 14,
  overflow: "hidden",
  border: "1px solid var(--border-soft)",
  boxShadow: "0 4px 14px rgba(59,10,20,0.08)"
};

const reportBtn = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 32,
  height: 32,
  border: "none",
  background: "none",
  color: "var(--text-primary)",
  fontSize: 22,
  cursor: "pointer"
};

const cardHeader = {
  display: "flex",
  alignItems: "center",
  gap: 24,
  padding: "4px 14px"
};

const cardAvatar = {
  width: 46,
  height: 46,
  borderRadius: "50%",
  objectFit: "cover",
  border: "1px solid var(--border-medium)"
};

const image = {
  width: "100%",
  height: "auto",
  display: "block"
};

const content = { padding: "12px 12px 4px" };

const usernameLink = {
  fontWeight: 700,
  color: "var(--text-primary)",
  textDecoration: "none",
  fontSize: 18
};

const caption = {
  margin: "4px 0",
  fontSize: 13.5,
  color: "var(--text-secondary)",
  whiteSpace: "pre-wrap"
};

const actions = {
  display: "flex",
  alignItems: "center",
  gap: 2
};

const commentBadgeBtn = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  background: "none",
  border: "none",
  borderRadius: 20,
  height: 40,
  padding: "0 6px",
  fontSize: 14,
  fontWeight: 700,
  color: "var(--text-primary)",
  cursor: "pointer"
};

const commentCountText = {
  fontWeight: "bold",
  fontSize: 13,
  color: "var(--text-primary)"
};
